// Shared, presentational guess board: the controlled input + the scored attempt
// list with temperature bars. Extracted so the live daily game and the archive
// replay render the SAME core and can't drift (ARCHIVE_IMPLEMENTATION_PLAN F).
//
// Pure presentation: all state, scoring and animation orchestration stay with the
// caller. Colour/label are injected so the daily palette lives in one place and
// both callers pass the same helpers.
import React from "react";

// The same enter arrow the daily and mega inputs draw. It lives here because the
// button belongs to the input, and the input belongs to this component — kept in
// App.js it was simply forgotten on every board that goes through here.
const ENTER_ICON = (
  <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor"
    strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M20 6v5a3 3 0 0 1-3 3H6" />
    <polyline points="10 10 6 14 10 18" />
  </svg>
);

export default function GuessBoard({
  value,
  onChange,
  onSubmit,
  placeholder,
  // Accessible name for the send button. Injected like colorFor/labelFor — this
  // component has no translator of its own and is not going to grow one.
  submitLabel,
  disabled = false,
  readOnly = false,
  errorMessage,
  attempts = [],
  highlightedIndex = -1,
  colorFor,
  labelFor,
  inputRef,
  timeoutProgress = 0,
  lastAttempt = null,
  lastLabelFor,
  countLabel,
  maskWord = (w) => w,
  // The word travelling from the input down to its card, and the card it pushes
  // out on the way. Optional: a caller that does not animate simply never sets
  // them and the board renders exactly as before.
  flyingWord = null,
  leavingWord = null,
}) {
  const submit = () => {
    if (!disabled) onSubmit();
  };

  // How far the flying card has to travel to reach the input it came from. The
  // daily board has always measured this from the live input rather than guessing
  // a constant, because the distance changes with the error line, the cooldown
  // bar and the phone's font size. Same measurement here, same fallback.
  const flyingOffset = () => {
    const el = inputRef && inputRef.current;
    if (!el) return -150;
    const rect = el.getBoundingClientRect();
    return -(rect.bottom + window.scrollY + 20 - rect.top - 20);
  };

  return (
    <div className="guessboard">
      {/* readOnly: reviewing a finished day — show the guesses, no input box. */}
      {!readOnly && (
        <div className="input-container">
          <div className="input-wrapper">
            <input
              ref={inputRef}
              type="text"
              value={value}
              disabled={disabled}
              onChange={(e) => onChange(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") submit();
              }}
              placeholder={placeholder}
              className={`guess-input${value ? "" : " guess-input--empty"}`}
            />
            {/* The send button the daily and mega inputs have always had, and this
                board never did — reported 2026-08-28 on the group word. Both of
                those write their input out by hand, so the arrow was added twice
                there and nowhere here, which left the group round and the archive
                replay as the only places you could type a word with no visible way
                to send it. Enter still works; on a phone that is not obvious and on
                some keyboards it is not even offered.

                Shown only with something typed, exactly as on the other two: an
                arrow floating in an empty box invites a click that does nothing. */}
            {!disabled && (value || "").trim() && (
              <button
                type="button"
                className="guess-submit"
                aria-label={submitLabel}
                onClick={submit}
              >
                {ENTER_ICON}
              </button>
            )}
            {/* Cooldown bar, exactly like the daily board — the input never greys. */}
            {timeoutProgress > 0 && (
              <div className="timeout-progress" style={{ width: `${timeoutProgress}%` }}></div>
            )}
          </div>
        </div>
      )}

      {!readOnly && errorMessage && <div className="error-message">{errorMessage}</div>}

      {/* The word just entered gets its own highlighted card under the input, and
          still appears in the sorted list below — same two-place read as the daily
          board. The card names the temperature band, the list gives the number. */}
      {!readOnly && (flyingWord || leavingWord || lastAttempt) && (
        <div className="last-attempt-container">
          {/* The card being pushed out, animating away. */}
          {leavingWord && (
            <div
              className="attempt attempt-leaving"
              style={{
                background: `linear-gradient(to right, ${leavingWord.backgroundColor} ${leavingWord.similarity}%, var(--bar-track) 0%)`,
              }}
            >
              <div className="attempt_label">{maskWord(leavingWord.word)}</div>
              <div className="attempt_value">{leavingWord.label}</div>
            </div>
          )}

          {/* The word just submitted, sliding down out of the input. */}
          {flyingWord && (
            <div
              className="attempt attempt-flying"
              style={{
                background: `linear-gradient(to right, ${flyingWord.backgroundColor} ${flyingWord.similarity}%, var(--bar-track) 0%)`,
                "--input-offset": `${flyingOffset()}px`,
              }}
            >
              <div className="attempt_label">{maskWord(flyingWord.word)}</div>
              <div className="attempt_value">{flyingWord.label}</div>
            </div>
          )}

          {/* Where it lands, once nothing is in flight. */}
          {lastAttempt && !leavingWord && !flyingWord && (
            <div
              className="attempt attempt-last"
              style={{
                background: `linear-gradient(to right, ${colorFor(lastAttempt.adjustedSimilarity)} ${lastAttempt.adjustedSimilarity}%, var(--bar-track) 0%)`,
              }}
            >
              <div className="attempt_label">{maskWord(lastAttempt.guess)}</div>
              <div className="attempt_value">
                {(lastLabelFor || labelFor)(lastAttempt.adjustedSimilarity)}
              </div>
            </div>
          )}
        </div>
      )}

      {!readOnly && (flyingWord || leavingWord || lastAttempt) && attempts.length > 1 && countLabel && (
        <div className="separator separator-animate">
          <span className="separator-pill">{countLabel}</span>
        </div>
      )}

      {/* Match the daily board: while playing, the first guess lives ONLY in the
          big last-attempt card — the sorted list (below the separator) appears from
          the second guess on. In review (readOnly) or when there's no last-attempt
          card, show the list even for a single guess so it never disappears. */}
      {attempts.length > 0 &&
        (readOnly || !(lastAttempt || flyingWord || leavingWord) || attempts.length > 1) && (
        <div className="attempt-list">
          {attempts.map((a, i) => (
            <div
              key={i}
              className={`attempt ${i === highlightedIndex ? "attempt-last" : ""}`}
              title={`${a.adjustedSimilarity.toFixed(2)}%`}
              style={{
                background: `linear-gradient(to right, ${colorFor(a.adjustedSimilarity)} ${a.adjustedSimilarity}%, var(--bar-track) 0%)`,
              }}
            >
              <div className="attempt_label">{maskWord(a.guess)}</div>
              <div className="attempt_value">{labelFor(a.adjustedSimilarity)}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
